import React, { useMemo, useState, useEffect } from "react";
import { Card, CardBody, CardTitle, Input, Button, Table, Badge, Row, Col, CardHeader, Nav, NavItem, NavLink, TabContent, TabPane, Spinner } from "reactstrap";
import { AiOutlineSearch, AiOutlineFileExcel } from "react-icons/ai";
import { FiTrendingUp, FiBarChart2, FiActivity } from "react-icons/fi";
import * as XLSX from "xlsx";
import axios from "axios";
import "./ReportManagement.css";

/**
 * Organization Report Management (Shared Component)
 * Displays per-organization totals and upload success rate, with search/filter and export.
 * 
 * @param {string} role - User role: 'subadmin', 'admin', or 'superadmin'
 * 
 * Access Control:
 * - SubAdmin: Only sees assigned organizations
 * - Admin/SuperAdmin: Sees ALL organizations
 */
const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => {
  // Try both 'token' and 'authToken' for backward compatibility
  return sessionStorage.getItem("token") || 
         localStorage.getItem("token") || 
         sessionStorage.getItem("authToken") || 
         localStorage.getItem("authToken");
};

const MONTHS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

export default function ReportManagement({ role = 'subadmin' }) {
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [recentActivityData, setRecentActivityData] = useState([]);

  // Determine API endpoints based on role
  const getApiEndpoint = (endpoint) => {
    const rolePrefix = role === 'superadmin' ? 'superadmin' : 
                       role === 'admin' ? 'admin' : 
                       'sub-admin';
    return `${API_URL}/${rolePrefix}${endpoint}`;
  };

  // Fetch data from API
  useEffect(() => {
    fetchReportsData();
  }, [role]);

  const fetchReportsData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      console.log("=== ORGANIZATION REPORTS DEBUG ===");
      console.log("Role:", role);
      console.log("API URL:", API_URL);
      console.log("Token present:", !!token);
      console.log("Organizations endpoint:", getApiEndpoint('/organizations'));
      console.log("Upload history endpoint:", getApiEndpoint('/data-upload/history'));
      
      if (!token) {
        console.error("❌ No authentication token found!");
        console.error("Please check if user is logged in and token is stored in sessionStorage");
        setData([]);
        setRecentActivityData([]);
        setLoading(false);
        return;
      }
      
      // Decode token to see user info (for debugging)
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        console.log("✅ Decoded token user ID:", decoded.id);
        console.log("✅ Decoded token role:", decoded.role);
        console.log("✅ Decoded token email:", decoded.email);
      } catch (e) {
        console.error("Failed to decode token:", e);
      }
      
      // Fetch organizations and upload history
      console.log("📡 Fetching data from API...");
      const requests = [
        axios.get(getApiEndpoint('/organizations'), {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 1, limit: 100 }
        }),
        axios.get(getApiEndpoint('/data-upload/history'), {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 1, limit: 1000 }
        }),
      ];

      // For admin/superadmin, also fetch live transactions so recharge totals are always current.
      const canFetchTransactions = role === 'superadmin' || role === 'admin';
      const canFetchSubAdmins = role === 'superadmin' || role === 'admin';
      if (canFetchTransactions) {
        const txEndpoint = role === 'superadmin'
          ? `${API_URL}/superadmin/org-transactions`
          : `${API_URL}/admin/transactions`;
        requests.push(
          axios.get(txEndpoint, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: 1, limit: 5000, type: 'credit' }
          })
        );
      }

      if (canFetchSubAdmins) {
        const subAdminsEndpoint = role === 'superadmin'
          ? `${API_URL}/superadmin/sub-admin`
          : `${API_URL}/admin/sub-admins`;
        requests.push(
          axios.get(subAdminsEndpoint, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: 1, limit: 1000, status: 'all' }
          })
        );
      }

      const responses = await Promise.all(requests);
      const orgsRes = responses[0];
      const uploadHistoryRes = responses[1];
      const txRes = canFetchTransactions ? responses[2] : undefined;
      const subAdminsRes = canFetchSubAdmins
        ? responses[canFetchTransactions ? 3 : 2]
        : undefined;

      console.log("✅ API responses received!");
      console.log("Raw Organizations response:", orgsRes.data);
      console.log("Raw Upload history response:", uploadHistoryRes.data);

      console.log("=== SUBADMIN REPORTS DEBUG ===");
      console.log("Organizations response:", orgsRes.data);
      console.log("Upload history response:", uploadHistoryRes.data);
      console.log("Upload history response structure:", {
        hasData: !!uploadHistoryRes.data?.data,
        hasBatches: !!uploadHistoryRes.data?.batches,
        isArray: Array.isArray(uploadHistoryRes.data),
        dataIsArray: Array.isArray(uploadHistoryRes.data?.data),
        batchesIsArray: Array.isArray(uploadHistoryRes.data?.batches)
      });
      
      const organizations = (orgsRes.data?.data || orgsRes.data?.batches || orgsRes.data) || [];
      
      // Handle different response structures for upload history
      let uploadHistory = [];
      if (Array.isArray(uploadHistoryRes.data)) {
        uploadHistory = uploadHistoryRes.data;
      } else if (Array.isArray(uploadHistoryRes.data?.data)) {
        uploadHistory = uploadHistoryRes.data.data;
      } else if (Array.isArray(uploadHistoryRes.data?.batches)) {
        uploadHistory = uploadHistoryRes.data.batches;
      } else if (uploadHistoryRes.data?.data?.batches && Array.isArray(uploadHistoryRes.data.data.batches)) {
        uploadHistory = uploadHistoryRes.data.data.batches;
      } else {
        console.warn("Upload history response structure not recognized:", uploadHistoryRes.data);
        uploadHistory = [];
      }
      
      console.log("Parsed organizations count:", organizations.length);
      console.log("Parsed organizations:", organizations);
      console.log("Parsed upload history count:", uploadHistory.length);
      console.log("Parsed upload history:", uploadHistory);

      const transactionRows = Array.isArray(txRes?.data?.data)
        ? txRes.data.data
        : Array.isArray(txRes?.data)
          ? txRes.data
          : [];
      console.log("Parsed transaction rows count:", transactionRows.length);

      const subAdmins = Array.isArray(subAdminsRes?.data?.data)
        ? subAdminsRes.data.data
        : Array.isArray(subAdminsRes?.data)
          ? subAdminsRes.data
          : [];
      const subAdminById = subAdmins.reduce((acc, item) => {
        if (item?.id) acc[item.id] = item;
        return acc;
      }, {});
      
      // Extract wallet data from organizations (if included in the response)
      const walletMap = {};
      organizations.forEach(org => {
        if (org.wallet) {
          walletMap[org.id] = org.wallet;
        }
      });
      console.log("Wallet data from organizations:", walletMap);

      // Build recharge totals from transaction rows as a reliable source.
      const rechargeByOrg = {};
      transactionRows.forEach((tx) => {
        const orgId = tx.organizationId;
        if (!orgId) return;
        if (!rechargeByOrg[orgId]) {
          rechargeByOrg[orgId] = {
            totalRecharge: 0,
            lastRechargeAmount: 0,
            lastRechargeDate: null,
          };
        }
        const amount = Number(tx.amount || 0);
        rechargeByOrg[orgId].totalRecharge += amount;
        if (!rechargeByOrg[orgId].lastRechargeDate || new Date(tx.createdAt) > new Date(rechargeByOrg[orgId].lastRechargeDate)) {
          rechargeByOrg[orgId].lastRechargeDate = tx.createdAt;
          rechargeByOrg[orgId].lastRechargeAmount = amount;
        }
      });
      
      if (organizations.length === 0) {
        if (role === 'subadmin') {
          console.warn("⚠️ NO ORGANIZATIONS FOUND - This subadmin is not assigned to any organizations");
          console.warn("Please ensure organizations have either:");
          console.warn("1. assignee field set to this subadmin's ID");
          console.warn("2. OR createdBy field set to this subadmin's ID");
        } else {
          console.warn("⚠️ NO ORGANIZATIONS FOUND - No organizations exist in the system");
        }
      }

      // Calculate metrics by organization
      const orgMetrics = {};
      
      // Initialize all organizations
      organizations.forEach(org => {
        // Check if this organization has wallet data
        const wallet = walletMap[org.id];
        const fallbackSubAdmin =
          subAdminById[org.assignee] ||
          subAdminById[org.createdBy] ||
          null;
        
        const txMetrics = rechargeByOrg[org.id] || {};

        orgMetrics[org.id] = {
          organizationName: org.organizationName,
          agentCode: org.assignedSubAdmin?.employeeId || fallbackSubAdmin?.employeeId || '-',
          agentName: org.assignedSubAdmin?.fullName || fallbackSubAdmin?.fullName || '-',
          walletRechargeTotal: txMetrics.totalRecharge ?? wallet?.totalRecharge ?? wallet?.totalCredits ?? 0,
          walletBalance: wallet?.balance || 0,
          uploadsSuccessful: 0,
          uploadsTotal: 0,
          updatedAt: org.updatedAt || new Date().toISOString(),
          lastUploadDate: null,
          lastRechargeAmount: txMetrics.lastRechargeAmount ?? wallet?.lastRechargeAmount ?? 0,
          lastRechargeDate: txMetrics.lastRechargeDate ?? wallet?.lastRechargeDate ?? null,
          lastUploadBatch: null, // Track the most recent upload batch
          totalRecords: 0,
          validRecords: 0,
          invalidRecords: 0,
        };
      });

      // Note: If wallet data doesn't include recharge totals, we'll need to calculate from transactions
      // For now, we're using whatever wallet data is available

      // Process upload history - track last upload batch data
      uploadHistory.forEach(upload => {
        const orgId = upload.organizationId;
        if (orgMetrics[orgId]) {
          orgMetrics[orgId].uploadsTotal += 1;
          
          // Count successful uploads (status = 'completed' or 'success')
          if (upload.status === 'completed' || upload.status === 'success') {
            orgMetrics[orgId].uploadsSuccessful += 1;
          }
          
          // Track latest upload date and batch data
          const uploadDate = new Date(upload.createdAt || upload.updatedAt);
          if (!orgMetrics[orgId].lastUploadDate || uploadDate > new Date(orgMetrics[orgId].lastUploadDate)) {
            orgMetrics[orgId].lastUploadDate = upload.createdAt || upload.updatedAt;
            orgMetrics[orgId].updatedAt = upload.createdAt || upload.updatedAt;
            orgMetrics[orgId].lastUploadBatch = upload;
            // Use the latest batch's record counts
            orgMetrics[orgId].totalRecords = upload.totalRecords || 0;
            orgMetrics[orgId].validRecords = upload.validRecords || 0;
            orgMetrics[orgId].invalidRecords = upload.invalidRecords || 0;
          }
        }
      });

      // Convert metrics object to array
      const summaryData = Object.values(orgMetrics);
      
      // Transform for recent activity - only include orgs with uploads
      const activityData = summaryData
        .filter(org => org.uploadsTotal > 0)
        .map(org => ({
          organizationName: org.organizationName,
          agentCode: org.agentCode,
          agentName: org.agentName,
          lastUploadDate: org.lastUploadDate, // Don't use default date if no uploads
          lastRechargeAmount: org.lastRechargeAmount,
          lastRechargeDate: org.lastRechargeDate,
        }));

      setData(summaryData);
      setRecentActivityData(activityData);
    } catch (error) {
      console.error('❌ Error fetching Organization reports:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      });
      
      if (error.response?.status === 401) {
        console.error('🔒 Authentication failed - token may be invalid or expired');
      } else if (error.response?.status === 404) {
        console.error('🔍 Endpoint not found - check API URL configuration');
      } else if (error.response?.status === 403) {
        console.error('⛔ Access forbidden - user may not have required permissions');
      }
      
      setData([]);
      setRecentActivityData([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data
      .map((item) => {
        // Calculate percentage based on last upload batch's valid records
        const successRate = item.totalRecords > 0
          ? Math.round((item.validRecords / item.totalRecords) * 100)
          : 0;
        return { ...item, successRate };
      })
      .filter((item) => {
        const matchesSearch = !term || item.organizationName.toLowerCase().includes(term);
        let matchesDate = true;
        if (filterMonth || filterYear) {
          const d = item.updatedAt ? new Date(item.updatedAt) : null;
          if (!d) matchesDate = false;
          if (matchesDate && filterMonth && d.getMonth() + 1 !== Number(filterMonth)) {
            matchesDate = false;
          }
          if (matchesDate && filterYear && d.getFullYear() !== Number(filterYear)) {
            matchesDate = false;
          }
        }
        return matchesSearch && matchesDate;
      });
  }, [data, search, filterMonth, filterYear]);

  // Filter recent activity data
  const filteredRecentActivity = useMemo(() => {
    const term = search.trim().toLowerCase();
    return recentActivityData.filter((item) => {
      const matchesSearch = !term || item.organizationName.toLowerCase().includes(term);
      let matchesDate = true;
      if (filterMonth || filterYear) {
        const d = item.lastUploadDate ? new Date(item.lastUploadDate) : null;
        if (!d) matchesDate = false;
        if (matchesDate && filterMonth && d.getMonth() + 1 !== Number(filterMonth)) {
          matchesDate = false;
        }
        if (matchesDate && filterYear && d.getFullYear() !== Number(filterYear)) {
          matchesDate = false;
        }
      }
      return matchesSearch && matchesDate;
    });
  }, [recentActivityData, search, filterMonth, filterYear]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalAmount = filtered.reduce((sum, item) => sum + item.walletRechargeTotal, 0);
    const avgSuccessRate = filtered.length > 0
      ? Math.round(filtered.reduce((sum, item) => sum + item.successRate, 0) / filtered.length)
      : 0;
    const totalRecords = filtered.reduce((sum, item) => sum + item.totalRecords, 0);
    const validRecords = filtered.reduce((sum, item) => sum + item.validRecords, 0);
    return { totalAmount, avgSuccessRate, totalRecords, validRecords };
  }, [filtered]);

  const handleExport = () => {
    if (activeTab === "1") {
      const rows = filtered.map((row) => ({
        "Organization Name": row.organizationName,
        "Agent Code": row.agentCode,
        "Agent Name": row.agentName,
        "Wallet Recharge Total": row.walletRechargeTotal,
        "Valid Records %": row.successRate,
        "Valid Records": row.validRecords,
        "Total Records": row.totalRecords,
        "Last Upload Date": row.lastUploadDate ? new Date(row.lastUploadDate).toLocaleDateString('en-GB') : 'N/A',
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
      XLSX.writeFile(workbook, "subadmin-report.xlsx");
    } else {
      const rows = filteredRecentActivity.map((row) => ({
        "Organization Name": row.organizationName,
        "Agent Code": row.agentCode,
        "Agent Name": row.agentName,
        "Last Upload Date": row.lastUploadDate,
        "Last Recharge Amount": row.lastRechargeAmount,
        "Last Recharge Date": row.lastRechargeDate,
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Recent Activity");
      XLSX.writeFile(workbook, "recent-activity.xlsx");
    }
  };

  return (
    <div className="container-fluid">
      {/* Summary Cards */}
      <Row className="mb-4 g-3">
        <Col md="6">
          <Card className="shadow-sm border-0 bg-gradient rm-card-success">
            <CardBody className="py-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small fw-semibold">Total Amount Recharged</p>
                  <h3 className="mb-0 text-success fw-bold">
                     {summaryMetrics.totalAmount.toLocaleString()}
                  </h3>
                  <small className="text-muted">All Organizations</small>
                </div>
                <div className="rm-icon-success">
                  <FiTrendingUp />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card className="shadow-sm border-0 bg-gradient rm-card-primary">
            <CardBody className="py-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small fw-semibold">Average Valid Records Rate</p>
                  <h3 className="mb-0 text-primary fw-bold">
                    {summaryMetrics.avgSuccessRate}%
                  </h3>
                  <small className="text-muted">
                    ({summaryMetrics.validRecords} / {summaryMetrics.totalRecords} records)
                  </small>
                </div>
                <div className="rm-icon-primary">
                  <FiBarChart2 />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Report Table */}
      <Card className="shadow-sm border-0">
        <CardHeader className="bg-light border-bottom">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <CardTitle tag="h5" className="mb-0">
              <span className="text-dark fw-bold">Reports</span>
            </CardTitle>
            <div className="d-flex gap-2">
              <Button 
                color="info" 
                size="sm" 
                onClick={fetchReportsData}
                disabled={loading}
                className="d-flex align-items-center gap-2"
              >
                {loading ? <Spinner size="sm" /> : "🔄"} Refresh
              </Button>
              <Button color="success" size="sm" onClick={handleExport} className="d-flex align-items-center gap-2">
                <AiOutlineFileExcel /> Export to Excel
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Tabs Navigation */}
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={activeTab === "1" ? "active" : ""}
                onClick={() => setActiveTab("1")}
                style={{ 
                  cursor: "pointer",
                  backgroundColor: activeTab === "1" ? "#0d6efd" : "#f8f9fa",
                  color: activeTab === "1" ? "#fff" : "#6c757d",
                  border: "1px solid #dee2e6",
                  borderBottom: activeTab === "1" ? "none" : "1px solid #dee2e6",
                  fontWeight: activeTab === "1" ? "600" : "normal"
                }}
              >
                Summary Report
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "2" ? "active" : ""}
                onClick={() => setActiveTab("2")}
                style={{ 
                  cursor: "pointer",
                  backgroundColor: activeTab === "2" ? "#0d6efd" : "#f8f9fa",
                  color: activeTab === "2" ? "#fff" : "#6c757d",
                  border: "1px solid #dee2e6",
                  borderBottom: activeTab === "2" ? "none" : "1px solid #dee2e6",
                  fontWeight: activeTab === "2" ? "600" : "normal"
                }}
              >
                <FiActivity className="me-1" /> Recent Activity
              </NavLink>
            </NavItem>
          </Nav>

          <Row className="g-2 mb-4">
            <Col md="8">
              <div className="rm-search-wrapper">
                <AiOutlineSearch className="position-absolute rm-search-icon" />
                <Input
                  placeholder="Search organization..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded rm-search-input"
                />
              </div>
            </Col>
            <Col md="2">
              <Input
                type="select"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="rounded"
              >
                <option value="">Month</option>
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </Input>
            </Col>
            <Col md="2">
              <Input
                type="select"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="rounded"
              >
                <option value="">Year</option>
                {[2024, 2025, 2026].map((yr) => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </Input>
            </Col>
          </Row>

          {/* Tab Content */}
          <TabContent activeTab={activeTab}>
            {/* Summary Report Tab */}
            <TabPane tabId="1">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading reports...</p>
                </div>
              ) : (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="fw-semibold text-dark rm-table-header">#</th>
                      <th className="fw-semibold text-dark rm-table-header">Organization Name</th>
                      <th className="fw-semibold text-dark rm-table-header">Total Amount Recharged</th>
                      <th className="fw-semibold text-dark rm-table-header">Valid Records %</th>
                      <th className="fw-semibold text-dark rm-table-header">Records (Valid/Total)</th>
                      <th className="fw-semibold text-dark rm-table-header">Last Updated</th>
                      <th className="fw-semibold text-dark rm-table-header">Agent Code</th>
                      <th className="fw-semibold text-dark rm-table-header">Agent Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-5">
                          {data.length === 0 ? (
                            role === 'subadmin' ? (
                              <div>
                                <p className="mb-2 fw-bold">⚠️ No Organizations Assigned</p>
                                <p className="mb-0 small">
                                  This subadmin account is not assigned to any organizations yet. 
                                  <br />
                                  Please contact your administrator to assign organizations to your account.
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="mb-2 fw-bold">⚠️ No Organizations Found</p>
                                <p className="mb-0 small">
                                  No organizations exist in the system yet.
                                  <br />
                                  Create organizations to see their reports here.
                                </p>
                              </div>
                            )
                          ) : (
                            <p className="mb-0">No matching records found for your search criteria</p>
                          )}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((row, idx) => (
                        <tr key={row.organizationName} className="border-bottom">
                          <td className="text-muted fw-semibold">{idx + 1}</td>
                          <td className="fw-semibold text-dark">{row.organizationName}</td>
                          <td>
                            <Badge color="primary" className="fs-6 px-3 py-2">
                               {row.walletRechargeTotal.toLocaleString()}
                            </Badge>
                          </td>
                          <td>
                            <Badge 
                              color={row.successRate >= 90 ? "success" : row.successRate >= 70 ? "warning" : "danger"}
                              className="fs-6 px-3 py-2"
                            >
                              {row.successRate}%
                            </Badge>
                          </td>
                          <td className="text-muted">
                            <small>{row.validRecords} / {row.totalRecords}</small>
                          </td>
                          <td className="text-muted">
                            <small>{row.lastUploadDate ? new Date(row.lastUploadDate).toLocaleDateString('en-GB') : 'N/A'}</small>
                          </td>
                          <td className="text-muted">{row.agentCode}</td>
                          <td className="text-muted">{row.agentName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              )}

              {filtered.length > 0 && (
                <div className="mt-3 pt-3 border-top text-muted small">
                  <strong>Total Records:</strong> {filtered.length} organizations
                </div>
              )}
            </TabPane>

            {/* Recent Activity Tab */}
            <TabPane tabId="2">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading recent activity...</p>
                </div>
              ) : (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="fw-semibold text-dark rm-table-header">#</th>
                      <th className="fw-semibold text-dark rm-table-header">Organization Name</th>
                      <th className="fw-semibold text-dark rm-table-header">Last Upload Date</th>
                      <th className="fw-semibold text-dark rm-table-header">Last Recharge Amount</th>
                      <th className="fw-semibold text-dark rm-table-header">Last Recharge Date</th>
                      <th className="fw-semibold text-dark rm-table-header">Agent Code</th>
                      <th className="fw-semibold text-dark rm-table-header">Agent Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecentActivity.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          {data.length === 0 ? (
                            role === 'subadmin' ? (
                              <div>
                                <p className="mb-2 fw-bold">⚠️ No Organizations Assigned</p>
                                <p className="mb-0 small">
                                  This subadmin account is not assigned to any organizations yet. 
                                  <br />
                                  Please contact your administrator to assign organizations to your account.
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="mb-2 fw-bold">⚠️ No Organizations Found</p>
                                <p className="mb-0 small">
                                  No organizations exist in the system yet.
                                </p>
                              </div>
                            )
                          ) : recentActivityData.length === 0 ? (
                            <div>
                              <p className="mb-0">No upload activity recorded yet</p>
                              <small className="text-muted">Organizations will appear here once they start uploading data</small>
                            </div>
                          ) : (
                            <p className="mb-0">No matching activity found for your search criteria</p>
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredRecentActivity.map((row, idx) => (
                        <tr key={row.organizationName} className="border-bottom">
                          <td className="text-muted fw-semibold">{idx + 1}</td>
                          <td className="fw-semibold text-dark">{row.organizationName}</td>
                          <td className="text-muted">
                            <small>{row.lastUploadDate ? new Date(row.lastUploadDate).toLocaleDateString('en-GB') : 'N/A'}</small>
                          </td>
                          <td>
                            <Badge color="success" className="fs-6 px-3 py-2">
                              ₹ {row.lastRechargeAmount.toLocaleString()}
                            </Badge>
                          </td>
                          <td className="text-muted">
                            <small>{row.lastRechargeDate ? new Date(row.lastRechargeDate).toLocaleDateString('en-GB') : 'N/A'}</small>
                          </td>
                          <td className="text-muted">{row.agentCode}</td>
                          <td className="text-muted">{row.agentName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              )}

              {filteredRecentActivity.length > 0 && (
                <div className="mt-3 pt-3 border-top text-muted small">
                  <strong>Total Records:</strong> {filteredRecentActivity.length} organizations
                </div>
              )}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
}

